/**
 * Role-based access control: canonical English role slugs aligned with the backend API.
 * Role IDs (901000001–909000009) come from the backend profile; route guards and sidebar use these IDs.
 */

export type Role =
  | 'platform_owner'
  | 'general_manager'
  | 'regional_manager'
  | 'dispatcher'
  | 'tenant_owner'
  | 'cashier';

/** Role ID (backend): 90 X 00 00 0X */
export const ROLE_IDS = {
  platform_owner: 901000001,
  general_manager: 902000002,
  regional_manager: 903000003,
  dispatcher: 904000004,
  tenant_owner: 908000008,
  cashier: 909000009,
} as const;

export type RoleId = (typeof ROLE_IDS)[keyof typeof ROLE_IDS];

/** Backend may emit alternate numeric IDs for some roles */
const ADDITIONAL_ROLE_IDS: Record<number, Role> = {
  905000005: 'tenant_owner',
  906000006: 'tenant_owner',
  907000007: 'tenant_owner',
  903000006: 'tenant_owner',
  908000006: 'tenant_owner',
  908000008: 'tenant_owner',
  909000006: 'cashier',
};

const ROLE_ID_TO_ROLE: Record<number, Role> = {
  [ROLE_IDS.platform_owner]: 'platform_owner',
  [ROLE_IDS.general_manager]: 'general_manager',
  [ROLE_IDS.regional_manager]: 'regional_manager',
  [ROLE_IDS.dispatcher]: 'dispatcher',
  [ROLE_IDS.tenant_owner]: 'tenant_owner',
  [ROLE_IDS.cashier]: 'cashier',
  ...ADDITIONAL_ROLE_IDS,
};

/** Resolve Role from backend role_id (901000001–909000009) or role string */
export function getRoleFromRoleId(roleIdOrRole: number | string | undefined | null): Role {
  // Security: never default to a high-privilege role when backend payload is missing/unknown.
  if (roleIdOrRole == null) return 'cashier';
  const asString = String(roleIdOrRole).trim().toLowerCase();
  const STRING_ROLE_ALIASES: Record<string, Role> = {
    platform_owner: 'platform_owner',
    general_manager: 'general_manager',
    regional_manager: 'regional_manager',
    dispatcher: 'dispatcher',
    tenant_owner: 'tenant_owner',
    cashier: 'cashier',
    cashier_operator: 'cashier',
    operator: 'cashier',
    кассир: 'cashier',
    клиент: 'tenant_owner',
    владелец: 'tenant_owner',
    owner: 'tenant_owner',
    client: 'tenant_owner',
    customer: 'tenant_owner',
    store_owner: 'tenant_owner',
    manager: 'regional_manager',
    region_manager: 'regional_manager',
    super_admin: 'platform_owner',
    ega: 'platform_owner',
    bosh_menejer: 'general_manager',
    viloyat_menejer: 'regional_manager',
    dispecher: 'dispatcher',
    mijoz: 'tenant_owner',
    kassir: 'cashier',
  };
  if (STRING_ROLE_ALIASES[asString]) return STRING_ROLE_ALIASES[asString];
  const n = typeof roleIdOrRole === 'number' ? roleIdOrRole : parseInt(asString, 10);
  if (!Number.isNaN(n) && ROLE_ID_TO_ROLE[n]) return ROLE_ID_TO_ROLE[n];
  return 'cashier';
}

export function getRoleId(role: Role): number {
  return ROLE_IDS[role] ?? ROLE_IDS.platform_owner;
}

export const ROLES: Record<Role, string> = {
  platform_owner: 'Ega (Loyiha egasi)',
  general_manager: 'Bosh menejer',
  regional_manager: 'Viloyat menejer',
  dispatcher: 'Dispecher',
  tenant_owner: 'Joy egasi (dastur sotib olgan)',
  cashier: 'Kassir',
};

export const ROLE_LIST: Role[] = [
  'platform_owner',
  'general_manager',
  'regional_manager',
  'dispatcher',
  'tenant_owner',
  'cashier',
];
